import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppTextField } from "../form";
import { AppFormField } from "../layout";
import {
  chartOptionOutlinedInputSx,
  GRID_HALF,
} from "../../lib/ui/formLayout";
import ChartOptionFieldShell from "./ChartOptionFieldShell";
import {
  PLACEMENT_HORIZONTAL,
  PLACEMENT_VERTICAL,
  type PlacementH,
  type PlacementV,
} from "./chartOptionLayout";

type PlacementOption = { value: string; label: string };

type Props = {
  horizontal: PlacementH;
  vertical: PlacementV;
  horizontalLabelKey: string;
  verticalLabelKey: string;
  disabled?: boolean;
  onChange: (next: { horizontal: PlacementH; vertical: PlacementV }) => void;
};

/** Two half-width select fields — must be rendered as direct children of AppFormGrid. */
export default function ChartPlacementSelects({
  horizontal,
  vertical,
  horizontalLabelKey,
  verticalLabelKey,
  disabled,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const horizontalLabel = t(horizontalLabelKey);
  const verticalLabel = t(verticalLabelKey);

  const horizontalOptions = useMemo<PlacementOption[]>(
    () =>
      PLACEMENT_HORIZONTAL.map((value) => ({
        value,
        label: t(`chartOptions.pos.${value}`),
      })),
    [t]
  );

  const verticalOptions = useMemo<PlacementOption[]>(
    () =>
      PLACEMENT_VERTICAL.map((value) => ({
        value,
        label: t(`chartOptions.pos.${value}`),
      })),
    [t]
  );

  return (
    <>
      <AppFormField size={GRID_HALF} align="stretch">
        <ChartOptionFieldShell label={horizontalLabel} disabled={disabled}>
          <AppAutocomplete<PlacementOption>
            label={horizontalLabel}
            options={horizontalOptions}
            value={horizontalOptions.find((o) => o.value === horizontal) ?? horizontalOptions[0]}
            disabled={disabled}
            onChange={(_, opt) =>
              opt && onChange({ horizontal: opt.value as PlacementH, vertical })
            }
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
            renderInput={(params) => (
              <AppTextField
                {...params}
                hiddenLabel
                label={horizontalLabel}
                margin="none"
                sx={chartOptionOutlinedInputSx}
              />
            )}
            sx={chartOptionOutlinedInputSx}
          />
        </ChartOptionFieldShell>
      </AppFormField>
      <AppFormField size={GRID_HALF} align="stretch">
        <ChartOptionFieldShell label={verticalLabel} disabled={disabled}>
          <AppAutocomplete<PlacementOption>
            label={verticalLabel}
            options={verticalOptions}
            value={verticalOptions.find((o) => o.value === vertical) ?? verticalOptions[0]}
            disabled={disabled}
            onChange={(_, opt) =>
              opt && onChange({ horizontal, vertical: opt.value as PlacementV })
            }
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
            renderInput={(params) => (
              <AppTextField
                {...params}
                hiddenLabel
                label={verticalLabel}
                margin="none"
                sx={chartOptionOutlinedInputSx}
              />
            )}
            sx={chartOptionOutlinedInputSx}
          />
        </ChartOptionFieldShell>
      </AppFormField>
    </>
  );
}
